/**
 * Studio (30.03.08 slice 1) — the owner's customization app.
 *
 * Two cards over the model API's Studio routes (server/src/api/modelRoutes.js):
 *
 *   modelOverlayCard   one draft overlay — a JSON patch against one model key
 *   modelStatusCard    the org's customization state as one record, with the
 *                      drafts validated every time it loads, and the lifecycle
 *                      actions as ordinary card-title post actions
 *
 * The only new input is `json`, the patch editor. Everything else is the
 * framework every other app uses.
 */
export const studioCards = {
    /*
     * One custom field (30.03.09 §2). Saving writes the five overlays a field
     * needs (schema, search, card, list, labels) as drafts; the overview's
     * Deploy makes them live. Target, name and type are fixed once created:
     * records hold values of that type.
     */
    modelFieldCard: {
        entity: 'modelField',
        path: 'modelField',
        titleBar: { label: 'studio.field.heading', mergeParams: ['label'], new: 'studio.field.new.title' },
        cardTitle: {
            title: 'all.form.button.more',
            menuItems: [
                {
                    action: 'delete',
                    label: 'studio.field.menu.hide',
                    confirm: true,
                    modalConfig: 'modelFieldConfirmDelete'
                }
            ]
        },
        width: '768px',
        sections: [
            {
                id: 'modelField',
                meta: {
                    type: 'form',
                    width: '768px'
                },
                groups: [
                    {
                        type: 'description',
                        id: 'fieldHelp',
                        label: 'studio.field.form.help.label',
                        text: 'studio.field.form.help.text'
                    },
                    {
                        type: 'group',
                        id: 'fieldKey',
                        fields: [
                            {
                                type: 'select',
                                id: 'target',
                                displayField: 'targetName',
                                label: 'studio.field.form.target',
                                placeholder: 'studio.field.form.target.placeholder',
                                entity: 'modelFieldTarget',
                                labelLanguage: 'ui',
                                valueMap: {
                                    'target': 'id',
                                    'targetName': 'name'
                                },
                                options: { withHeaders: false },
                                required: true,
                                lockOnEdit: true
                            },
                            {
                                type: 'select',
                                id: 'type',
                                displayField: 'typeName',
                                label: 'studio.field.form.type',
                                placeholder: 'studio.field.form.type.placeholder',
                                entity: 'modelFieldType',
                                labelLanguage: 'ui',
                                valueMap: {
                                    'type': 'id',
                                    'typeName': 'name'
                                },
                                options: { withHeaders: false },
                                required: true,
                                lockOnEdit: true
                            }
                        ]
                    },
                    {
                        type: 'text',
                        id: 'fieldId',
                        label: 'studio.field.form.fieldId',
                        placeholder: 'studio.field.form.fieldId.placeholder',
                        required: true,
                        lockOnEdit: true
                    },
                    {
                        type: 'text',
                        id: 'label',
                        label: 'studio.field.form.label',
                        placeholder: 'studio.field.form.label.placeholder',
                        required: true
                    },
                    /*
                     * Where it shows: every place on the record's cards and
                     * lists, read from the model as this org sees it — its own
                     * fields included — or "not shown". Served from the model
                     * catalog (GET /modelFieldPlacement); `field` keeps a field
                     * from being offered a place after itself.
                     */
                    {
                        type: 'select',
                        id: 'cardPlacement',
                        displayField: 'cardPlacementName',
                        label: 'studio.field.form.cardPlacement',
                        placeholder: 'studio.field.form.cardPlacement.placeholder',
                        entity: 'modelFieldPlacement',
                        entityParams: { type: 'query', params: [{ key: 'target', value: '${target}' }, { key: 'kind', value: 'card' }, { key: 'field', value: '${fieldId}' }] },
                        labelLanguage: 'ui',
                        valueMap: {
                            'cardPlacement': 'id',
                            'cardPlacementName': 'name'
                        },
                        options: { withHeaders: false },
                        required: true,
                        linkedFields: ['target', 'fieldId']
                    },
                    {
                        type: 'select',
                        id: 'listPlacement',
                        displayField: 'listPlacementName',
                        label: 'studio.field.form.listPlacement',
                        placeholder: 'studio.field.form.listPlacement.placeholder',
                        entity: 'modelFieldPlacement',
                        entityParams: { type: 'query', params: [{ key: 'target', value: '${target}' }, { key: 'kind', value: 'list' }, { key: 'field', value: '${fieldId}' }] },
                        labelLanguage: 'ui',
                        valueMap: {
                            'listPlacement': 'id',
                            'listPlacementName': 'name'
                        },
                        options: { withHeaders: false },
                        required: true,
                        linkedFields: ['target', 'fieldId']
                    },
                    {
                        type: 'switch',
                        id: 'searchable',
                        label: 'studio.field.form.searchable',
                        defaultType: 'value',
                        defaultValue: false,
                        condition: { field: 'type', operator: 'equals', value: 'text' }
                    }
                ],
                buttons: [
                    {
                        type: 'submit',
                        id: 'modelFieldCard.modelField.save',
                        action: 'save',
                        label: 'all.form.button.update',
                        icon: 'save',
                        variant: 'primary'
                    }
                ],
                displaySection: {
                    emptyField: 'fieldId',
                    columns: [
                        {
                            weight: 1,
                            basis: '100%',
                            elements: [
                                {
                                    type: 'field',
                                    value: 'label',
                                    scale: 'title'
                                },
                                {
                                    type: 'field',
                                    value: 'fieldId',
                                    scale: 'normal'
                                },
                                {
                                    type: 'field',
                                    value: 'targetName',
                                    scale: 'normal'
                                }
                            ]
                        }
                    ]
                }
            },
            // The label in other languages; English is the label above, and a
            // language without a row shows it.
            {
                id: 'modelField.translations',
                meta: {
                    type: 'lines',
                    linesArray: 'translations',
                    linesAddAction: 'studio.field.form.translations.add',
                    sectionTitle: 'studio.field.form.translations',
                    width: '768px'
                },
                groups: [
                    {
                        type: 'group',
                        id: 'translation',
                        fields: [
                            {
                                type: 'select',
                                id: 'language',
                                displayField: 'languageName',
                                label: 'studio.field.form.translation.language',
                                placeholder: 'studio.field.form.translation.language.placeholder',
                                entity: 'language',
                                labelLanguage: 'ui',
                                valueMap: {
                                    'language': 'id',
                                    'languageName': 'name'
                                },
                                // No prefill: the first entry would be English, the
                                // one language a translation row cannot be.
                                options: { withHeaders: false, autoSelectFirst: false },
                                required: true,
                                defaultType: 'none'
                            },
                            {
                                type: 'text',
                                id: 'label',
                                label: 'studio.field.form.translation.label',
                                placeholder: 'studio.field.form.translation.label.placeholder',
                                required: true
                            }
                        ]
                    }
                ],
                buttons: [
                    {
                        type: 'button',
                        id: 'modelFieldCard.translations.delete',
                        action: 'delete',
                        label: 'all.form.button.delete',
                        icon: 'delete',
                        variant: 'danger',
                        size: 'icon'
                    },
                    {
                        type: 'button',
                        id: 'modelFieldCard.translations.save',
                        action: 'save',
                        label: 'all.form.button.save',
                        variant: 'primary'
                    }
                ],
                displaySection: {
                    emptyField: 'languageName',
                    emptyMessage: 'studio.field.form.translations.empty',
                    editButtonRightOffset: '50%',
                    stackOnMobile: true,
                    columns: [
                        {
                            weight: 4,
                            basis: '40%',
                            elements: [
                                {
                                    type: 'field',
                                    value: 'languageName',
                                    scale: 'normal'
                                }
                            ]
                        },
                        {
                            weight: 6,
                            basis: '60%',
                            elements: [
                                {
                                    type: 'field',
                                    value: 'label',
                                    scale: 'normal'
                                }
                            ]
                        }
                    ]
                }
            }
        ]
    },
    modelOverlayCard: {
        entity: 'modelOverlay',
        path: 'modelOverlay',
        titleBar: { label: 'studio.overlay.heading', mergeParams: ['namespace', 'modelKey'], new: 'studio.overlay.new.title' },
        cardTitle: {
            title: 'all.form.button.more',
            menuItems: [
                {
                    action: 'delete',
                    label: 'studio.overlay.menu.delete',
                    confirm: true,
                    modalConfig: 'modelOverlayConfirmDelete'
                }
            ]
        },
        width: '960px',
        sections: [
            {
                id: 'modelOverlay',
                meta: {
                    type: 'form',
                    width: '960px'
                },
                groups: [
                    {
                        type: 'group',
                        id: 'overlayKey',
                        fields: [
                            {
                                type: 'select',
                                id: 'namespace',
                                displayField: 'namespaceName',
                                label: 'studio.overlay.form.namespace',
                                placeholder: 'studio.overlay.form.namespace.placeholder',
                                entity: 'modelNamespace',
                                labelLanguage: 'ui',
                                valueMap: {
                                    'namespace': 'id',
                                    'namespaceName': 'name'
                                },
                                options: { withHeaders: false },
                                required: true,
                                lockOnEdit: true
                            },
                            /*
                             * Typed for screens, data and logic; picked for texts,
                             * from every text file the app ships, named
                             * ("Sales — English (en-US)") — GET /modelTxtFile.
                             */
                            {
                                type: 'text',
                                id: 'modelKey',
                                label: 'studio.overlay.form.modelKey',
                                placeholder: 'studio.overlay.form.modelKey.placeholder',
                                required: true,
                                lockOnEdit: true,
                                conditionals: [
                                    {
                                        condition: { field: 'namespace', operator: 'equals', value: 'txt' },
                                        set: {
                                            type: 'select',
                                            displayField: 'modelKeyName',
                                            entity: 'modelTxtFile',
                                            labelLanguage: 'ui',
                                            valueMap: { 'modelKey': 'id', 'modelKeyName': 'name' },
                                            options: { withHeaders: false, autoSelectFirst: false },
                                            defaultType: 'none'
                                        }
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        type: 'textarea',
                        id: 'notes',
                        label: 'studio.overlay.form.notes',
                        placeholder: 'studio.overlay.form.notes.placeholder',
                        rows: 2
                    },
                    {
                        type: 'description',
                        id: 'patchHelp',
                        label: 'studio.overlay.form.patch.help.label',
                        text: 'studio.overlay.form.patch.help.text'
                    },
                    {
                        type: 'json',
                        id: 'patch',
                        label: 'studio.overlay.form.patch',
                        required: true,
                        rows: 22
                    }
                ],
                buttons: [
                    {
                        type: 'submit',
                        id: 'modelOverlayCard.modelOverlay.save',
                        action: 'save',
                        label: 'all.form.button.update',
                        icon: 'save',
                        variant: 'primary'
                    }
                ],
                displaySection: {
                    emptyField: 'modelKey',
                    columns: [
                        {
                            weight: 1,
                            basis: '100%',
                            elements: [
                                {
                                    type: 'field',
                                    value: 'modelKey',
                                    scale: 'title'
                                },
                                {
                                    type: 'field',
                                    value: 'namespaceName',
                                    scale: 'normal'
                                },
                                {
                                    type: 'field',
                                    value: 'notes',
                                    scale: 'normal'
                                }
                            ]
                        }
                    ]
                }
            }
        ]
    },

    modelStatusCard: {
        entity: 'modelStatus',
        path: 'model/status',
        titleBar: { label: 'studio.status.heading', mergeParams: [], new: 'studio.status.heading' },
        cardTitle: {
            title: 'all.form.button.more',
            buttons: [
                {
                    id: 'modelStatusCard.validate',
                    action: 'validate',
                    label: 'studio.status.button.validate',
                    icon: 'checkMark',
                    variant: 'secondary'
                },
                {
                    id: 'modelStatusCard.preview',
                    action: 'preview',
                    label: 'studio.status.button.preview',
                    icon: 'play',
                    variant: 'secondary',
                    confirm: true,
                    modalConfig: 'modelStatusConfirmPreview'
                },
                {
                    id: 'modelStatusCard.deploy',
                    action: 'deploy',
                    label: 'studio.status.button.deploy',
                    icon: 'upload',
                    variant: 'primary',
                    confirm: true,
                    modalConfig: 'modelStatusConfirmDeploy'
                }
            ],
            menuItems: [
                {
                    action: 'discard',
                    label: 'studio.status.menu.discard',
                    confirm: true,
                    modalConfig: 'modelStatusConfirmDiscard'
                },
                {
                    action: 'reset',
                    label: 'studio.status.menu.reset',
                    confirm: true,
                    modalConfig: 'modelStatusConfirmReset'
                }
            ],
            // postActionFn reads the success toast from here; `reload` refreshes
            // the record — and with it the live diagnostics — in place.
            actions: {
                validate: { message: 'studio.status.action.validate.done', reload: true },
                preview: { message: 'studio.status.action.preview.done', reload: true },
                deploy: { message: 'studio.status.action.deploy.done', reload: true },
                discard: { message: 'studio.status.action.discard.done', reload: true },
                reset: { message: 'studio.status.action.reset.done', reload: true }
            }
        },
        width: '960px',
        showBack: false,
        sections: [
            {
                id: 'modelStatus',
                meta: {
                    type: 'form',
                    width: '960px'
                },
                groups: [
                    {
                        type: 'description',
                        id: 'intro',
                        label: 'studio.status.intro.label',
                        text: 'studio.status.intro.text'
                    },
                    {
                        type: 'group',
                        id: 'versions',
                        fields: [
                            {
                                type: 'text-readOnly',
                                id: 'activeModelVersion',
                                label: 'studio.status.form.active'
                            },
                            {
                                type: 'text-readOnly',
                                id: 'previewModelVersion',
                                label: 'studio.status.form.preview'
                            }
                        ]
                    },
                    {
                        type: 'group',
                        id: 'counts',
                        fields: [
                            {
                                type: 'text-readOnly',
                                displayType: 'quantity',
                                id: 'pendingCount',
                                label: 'studio.status.form.pending'
                            },
                            {
                                type: 'text-readOnly',
                                displayType: 'quantity',
                                id: 'errorCount',
                                label: 'studio.status.form.errors'
                            },
                            {
                                type: 'text-readOnly',
                                displayType: 'quantity',
                                id: 'warningCount',
                                label: 'studio.status.form.warnings'
                            }
                        ]
                    },
                    {
                        type: 'json',
                        id: 'diagnostics',
                        label: 'studio.status.form.diagnostics',
                        readOnly: true,
                        rows: 14
                    }
                ]
            }
        ]
    },

    // One published version, read-only, with Rollback — owner-only on the server.
    modelVersionCard: {
        entity: 'modelVersion',
        path: 'model/versions',
        titleBar: { label: 'studio.version.heading', mergeParams: ['id'], new: 'studio.version.heading' },
        cardTitle: {
            title: 'all.form.button.more',
            buttons: [
                {
                    // Not on the version that is already live: rolling back to it does nothing.
                    condition: { field: 'isActive', operator: 'notEquals', value: true },
                    id: 'modelVersionCard.rollback',
                    action: 'rollback',
                    label: 'studio.version.button.rollback',
                    icon: 'reset',
                    variant: 'primary',
                    confirm: true,
                    modalConfig: 'modelVersionConfirmRollback'
                }
            ],
            actions: {
                rollback: { message: 'studio.version.action.rollback.done', reload: true }
            }
        },
        width: '960px',
        sections: [
            {
                id: 'modelVersion',
                meta: {
                    type: 'form',
                    width: '960px'
                },
                groups: [
                    {
                        type: 'group',
                        id: 'published',
                        fields: [
                            {
                                type: 'text-readOnly',
                                id: 'channel',
                                label: 'studio.version.form.channel'
                            },
                            {
                                type: 'text-readOnly',
                                displayType: 'datetime',
                                id: 'publishedAt',
                                label: 'studio.version.form.publishedAt'
                            },
                            {
                                type: 'text-readOnly',
                                id: 'baseVersion',
                                label: 'studio.version.form.baseVersion'
                            }
                        ]
                    },
                    {
                        type: 'json',
                        id: 'sourceOverlays',
                        label: 'studio.version.form.sourceOverlays',
                        readOnly: true,
                        rows: 5
                    },
                    {
                        type: 'json',
                        id: 'diagnostics',
                        label: 'studio.version.form.diagnostics',
                        readOnly: true,
                        rows: 10
                    }
                ]
            }
        ]
    }
}
